import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api, ApiFehler } from '../api/client'
import type { Schrank, Vollstaendigkeit } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import { beschreibe, fachName } from '../komponenten/beschreibung'

/**
 * Fachuebersicht eines Schranks. Hier laeuft die Vollstaendigkeitspruefung (P6-01): fehlende
 * Faecher werden benannt und lassen sich gezielt nachholen (P6-03). Abgeschlossen wird erst,
 * wenn alles da ist oder die Abweichung bewusst uebergangen wird (P6-02).
 */
export default function SchrankDetail() {
  const { schrankId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()
  const [fehler, setFehler] = useState<string | null>(null)
  const [notizOffen, setNotizOffen] = useState(false)
  const [notizEntwurf, setNotizEntwurf] = useState('')

  const schrank = useQuery({
    queryKey: ['schrank', schrankId],
    queryFn: () => api.get<Schrank>(`/api/schraenke/${schrankId}`),
  })

  const vollstaendigkeit = useQuery({
    queryKey: ['vollstaendigkeit', schrankId],
    queryFn: () => api.get<Vollstaendigkeit>(`/api/schraenke/${schrankId}/vollstaendigkeit`),
  })

  const loeschen = useMutation({
    mutationFn: () => api.loeschen(`/api/schraenke/${schrankId}`),
    onSuccess: () => navigiere(`/versorgungspunkte/${schrank.data?.versorgungspunktId ?? ''}`),
  })

  const notizSpeichern = useMutation({
    mutationFn: (text: string) => api.patch<Schrank>(`/api/schraenke/${schrankId}`, { notiz: text }),
    onSuccess: () => {
      abfragen.invalidateQueries({ queryKey: ['schrank', schrankId] })
      setNotizOffen(false)
    },
    onError: (ausnahme) =>
      setFehler(ausnahme instanceof ApiFehler ? ausnahme.message : 'Notiz konnte nicht gespeichert werden.'),
  })

  const abschliessen = useMutation({
    mutationFn: (uebergehen: boolean) =>
      api.post<Schrank>(`/api/schraenke/${schrankId}/abschliessen?uebergehen=${uebergehen}`),
    onSuccess: (aktualisiert) => {
      abfragen.invalidateQueries({ queryKey: ['versorgungspunkt'] })
      navigiere(`/versorgungspunkte/${aktualisiert.versorgungspunktId}`)
    },
    onError: (ausnahme) =>
      setFehler(ausnahme instanceof ApiFehler ? ausnahme.message : 'Abschluss nicht möglich.'),
  })

  const daten = schrank.data
  const fehlende = vollstaendigkeit.data?.fehlendeFaecher ?? []

  return (
    <AppRahmen
      titel={daten?.bezeichnung ?? 'Schrank'}
      unter={daten?.schrankCode}
      zurueck={daten ? `/versorgungspunkte/${daten.versorgungspunktId}` : '/'}
      pfad={daten?.pfad}
      ebene="schrank"
      menue={[
        {
          beschriftung: daten?.gesamtfotoVorhanden ? 'Gesamtfoto ersetzen' : 'Gesamtfoto aufnehmen',
          beiKlick: () => navigiere(`/schraenke/${schrankId}/gesamtfoto`),
        },
        {
          beschriftung: vollstaendigkeit.data?.vollstaendig ? 'Schrank abschließen' : 'Trotzdem abschließen',
          beiKlick: () => abschliessen.mutate(!vollstaendigkeit.data?.vollstaendig),
        },
        {
          beschriftung: 'Schrank umbenennen',
          eingabe: {
            feld: 'Bezeichnung',
            wert: daten?.bezeichnung ?? '',
            hinweis: `Die Kennung ${daten?.schrankCode ?? ''} und die Ablage der Fotos bleiben unverändert.`,
            speichern: async (bezeichnung) => {
              const neu = await api.patch<Schrank>(`/api/schraenke/${schrankId}`, { bezeichnung })
              abfragen.setQueryData(['schrank', schrankId], neu)
              // Der Name steht auch in Pfadleisten und Listen anderer Bildschirme.
              void abfragen.invalidateQueries()
            },
          },
        },
        {
          beschriftung: 'Schrank löschen',
          gefahr: true,
          bestaetigung: {
            frage: 'Alle Fächer und Fotos dieses Schranks werden entfernt.',
            bestaetigungstext: 'Endgültig löschen',
          },
          beiKlick: () => loeschen.mutate(),
        },
      ]}
      fuss={
        <div className="fuss-reihe">
          <button
            className="btn btn-primaer"
            onClick={() => navigiere(`/schraenke/${schrankId}/aufnahme/${naechsteNummer(daten, fehlende)}`)}
          >
            {daten && daten.anzahlFaecherErfasst === 0 ? 'Erstes Fach aufnehmen' : 'Weiter aufnehmen'}
          </button>
          <button
            className="btn btn-sekundaer schmal"
            onClick={() => {
              setNotizEntwurf(daten?.notiz ?? '')
              setNotizOffen(true)
            }}
          >
            Notizen
            {daten?.notiz ? <span className="punkt" aria-label="Notiz vorhanden" /> : null}
          </button>
        </div>
      }
    >
      {schrank.isPending && <div className="spinner" />}

      {daten && (
        <div className="gruppe">
          <div className="kopfzeile">
            <span className="label">
              Fächer: {daten.anzahlFaecherErfasst} aufgenommen, {daten.anzahlFaecherSoll} konfiguriert
            </span>
            {vollstaendigkeit.data?.vollstaendig ? (
              <span className="st st-ok">vollständig</span>
            ) : (
              <span className="st st-warn">
                {vollstaendigkeit.data?.fehlendeAnzahl === 1
                  ? '1 fehlt'
                  : `${vollstaendigkeit.data?.fehlendeAnzahl ?? 0} fehlen`}
              </span>
            )}
          </div>
          {daten.notiz && <p className="notiz">{daten.notiz}</p>}
        </div>
      )}

      {fehler && <p className="hinweiszeile">{fehler}</p>}

      <div className="fachliste">
        {daten?.faecher.map((fach) => (
          <button
            key={fach.id}
            className="fachzeile"
            onClick={() =>
              fach.bildObenVorhanden && fach.bildVorneVorhanden
                ? navigiere(`/faecher/${fach.id}/bestaetigen`)
                : navigiere(`/schraenke/${schrankId}/aufnahme/${fach.fachNr}`)
            }
          >
            {fach.bildObenVorhanden ? (
              <img src={`/api/faecher/${fach.id}/bild/oben`} alt={`Draufsicht Fach ${fach.fachNr}`} />
            ) : (
              <div style={{ width: 56, height: 42, borderRadius: 4, background: 'var(--gpi-blau-700-20)' }} />
            )}
            <div className="inh">
              <b>{fachName(fach)}</b>
              <span>
                {fach.nutzerErgebnis
                  ? beschreibe(fach.nutzerErgebnis)
                  : fach.kiVorschlag
                    ? `Vorschlag: ${beschreibe(fach.kiVorschlag)}`
                    : 'noch keine Aufnahme'}
              </span>
            </div>
            <span className={fach.status === 'bestaetigt' ? 'st st-ok' : 'st st-arbeit'}>
              {fach.status === 'bestaetigt' ? 'bestätigt' : 'offen'}
            </span>
          </button>
        ))}

        {/* Nur Nummern, zu denen es noch gar kein Fach gibt. Ein angelegtes, aber noch
            unvollständiges Fach steht bereits in der Liste darüber und würde sonst doppelt
            erscheinen - etwa nach dem Zurücksetzen eines Fachs. */}
        {fehlende
          .filter((nummer) => !daten?.faecher.some((fach) => fach.fachNr === nummer))
          .map((nummer) => (
            <button
              key={`fehlt-${nummer}`}
              className="fachzeile fehlt"
              onClick={() => navigiere(`/schraenke/${schrankId}/aufnahme/${nummer}`)}
            >
              <div style={{ width: 56, height: 42 }} />
              <div className="inh">
                <b>Fach {nummer}</b>
                <span>fehlt – jetzt nachholen</span>
              </div>
            </button>
          ))}
      </div>

      {/* Das Gesamtfoto steht bewusst unter der Fachliste: es dient der Zuordnung, die
          tägliche Arbeit findet aber in der Liste statt (Rückmeldung Iteration 3). */}
      {daten && (
        <div className="gruppe">
          <span className="label">Gesamtfoto</span>
          {daten.gesamtfotoVorhanden ? (
            <img
              src={`/api/schraenke/${daten.id}/gesamtfoto`}
              alt="Gesamtfoto des Schranks"
              style={{ borderRadius: 8, border: '1px solid var(--linie)' }}
            />
          ) : (
            <p className="sek">Noch kein Gesamtfoto vorhanden.</p>
          )}
          <button className="btn-dezent" onClick={() => navigiere(`/schraenke/${schrankId}/gesamtfoto`)}>
            {daten.gesamtfotoVorhanden ? 'Gesamtfoto ersetzen' : 'Gesamtfoto aufnehmen'}
          </button>
        </div>
      )}

      {notizOffen && (
        <div className="ueberlagerung" role="dialog" aria-modal="true" onClick={() => setNotizOffen(false)}>
          <div className="sheet" onClick={(ereignis) => ereignis.stopPropagation()}>
            <h3 className="s">Notizen</h3>
            <p className="sek">
              Freier Text zum Schrank, etwa Besonderheiten vor Ort oder offene Rückfragen.
            </p>
            <div className="feld">
              <textarea
                rows={6}
                value={notizEntwurf}
                autoFocus
                onChange={(ereignis) => setNotizEntwurf(ereignis.target.value)}
                placeholder="z. B. Schrank steht hinter der Tür, Schlüssel bei der Stationsleitung"
              />
            </div>
            <button
              className="btn btn-primaer"
              disabled={notizSpeichern.isPending}
              onClick={() => notizSpeichern.mutate(notizEntwurf)}
            >
              Speichern
            </button>
            <button className="btn-dezent" onClick={() => setNotizOffen(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </AppRahmen>
  )
}

function naechsteNummer(schrank: Schrank | undefined, fehlende: number[]): number {
  if (fehlende.length > 0) return fehlende[0]
  return (schrank?.faecher.length ?? 0) + 1
}
